import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon } from '@heroicons/react/24/outline';
import Link from '@/components/common/Link';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Footer4 } from './Footer4';

const footerFaqs = [
  {
    question: 'What does Turbo Labs do?',
    answer:
      'We run AI-first product sprints that ship live software in three weeks—design, engineering, automation, and change management moving in parallel.',
    cta: { label: 'Explore the studio', href: '/about' },
  },
  {
    question: 'How are you different?',
    answer:
      'Daily drip updates inside Ether let you review scope, commits, and runbooks as they land. We compound work on your feedback instead of waiting for standups.',
    cta: { label: 'See how delivery works', href: '/work' },
  },
  {
    question: 'What does it cost?',
    answer:
      'We scope the objects, workflows, and success targets on Day 1. Each sprint is priced on velocity bands, not billable hours—most launches land between $35k and $65k.',
    cta: { label: 'Talk pricing signals', href: '/contact' },
  },
  {
    question: 'Who have you worked with?',
    answer:
      'Energy companies, fintech platforms, SaaS scale-ups, and industrial operators use Turbo to launch production features and agent systems on tight timelines.',
    cta: { label: 'Browse recent wins', href: '/work' },
  },
  {
    question: 'Do you work in my industry?',
    answer:
      'If your business runs on repeatable workflows, we can automate it. We model real-world objects and triggers so domain context travels with every build.',
  },
  {
    question: 'Is the market really about to change?',
    answer:
      'Yes. Teams pairing humans with agents are shipping 10× faster and pulling away. We publish the signals and teardown how we deploy them each week.',
    cta: { label: 'Read the latest signal', href: '/blog/market-shift' },
  },
];

const keyServices = [
  {
    title: 'Accelerated SaaS Dev',
    description:
      'Stop wasting months on infrastructure. Launch your SaaS product in weeks with our battle-tested architecture.',
    href: '/services#saas',
  },
  {
    title: 'AI Systems',
    description:
      'Transform manual processes into intelligent workflows. Automate decisions and unlock hidden business value.',
    href: '/services#ai',
  },
  {
    title: 'Software Prototypes',
    description:
      "Don't risk building the wrong thing. Validate your ideas with real users in days, not months.",
    href: '/services#prototypes',
  },
  {
    title: 'Content Design',
    description:
      'End user confusion and dropoffs. Create intuitive experiences that guide and convert.',
    href: '/services#design',
  },
];

interface FooterProps {}

export const Footer: React.FC<FooterProps> = () => {
  const handleContactClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (typeof window !== 'undefined') {
      // @ts-expect-error custom drawer event dispatched globally
      window.dispatchEvent(new Event('open-contact'));
    }
  };

  return (
    // Make footer overlay readable in both themes with glass background
    <footer className="relative z-[300] pt-24 sm:pt-32 bg-white/80 dark:bg-zinc-950/70 backdrop-blur-xl">
      <div className="  space-y-8">
        {/* --- First Row --- */}
        <div className=" px-4 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Card: Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-2 p-6 md:p-8 bg-white/60 dark:bg-zinc-900/60 backdrop-blur-lg border border-zinc-200/50 dark:border-zinc-800/50 rounded-2xl"
          >
            <nav className="flex flex-col gap-y-4">
              {footerFaqs.map((item) => (
                <HoverCard key={item.question} openDelay={120} closeDelay={80}>
                  <HoverCardTrigger asChild>
                    <button
                      type="button"
                      className="group flex w-full items-center justify-between text-left text-xl text-zinc-600 transition-colors duration-200 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/20 dark:text-zinc-300 dark:hover:text-zinc-100 dark:focus-visible:ring-zinc-100/30"
                    >
                      <span>{item.question}</span>
                      <ArrowUpRightIcon className="h-6 w-6 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
                    </button>
                  </HoverCardTrigger>
                  <HoverCardContent
                    align="end"
                    side="top"
                    className="w-80 border border-zinc-200/60 bg-white/95 text-zinc-800 backdrop-blur-xl dark:border-zinc-700/60 dark:bg-zinc-900/95 dark:text-zinc-100"
                  >
                    <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-300">
                      {item.answer}
                    </p>
                    {item.cta && (
                      <Link
                        href={item.cta.href}
                        className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-zinc-900 transition-colors hover:text-zinc-700 dark:text-zinc-100 dark:hover:text-zinc-200"
                      >
                        {item.cta.label}
                        <ArrowUpRightIcon className="h-4 w-4" />
                      </Link>
                    )}
                  </HoverCardContent>
                </HoverCard>
              ))}
            </nav>
          </motion.div>

          {/* Right Card: Gradient CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col justify-between p-6 md:p-8 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 dark:from-primary-600 dark:to-primary-800 text-white h-[280px] md:h-[320px]"
          >
            <h3 className="text-xl font-light leading-snug">
              Ready to accelerate your vision?
            </h3>
            <Link
              href="/contact"
              onClick={handleContactClick}
              className="group inline-flex items-center gap-2 text-base font-normal self-start mt-auto" // Pushes to bottom
            >
              <span>Book a Discovery Call</span>
              <ArrowUpRightIcon className="w-5 h-5 transition-transform duration-300 group-hover:rotate-45" />
            </Link>
          </motion.div>
        </div>

        {/* --- Second Row --- */}
        <div className=" px-4 grid grid-cols-1 md:grid-cols-4 gap-8">
          {keyServices.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
            >
              <Link
                href={service.href}
                className="group flex flex-col h-full p-8 rounded-2xl bg-white/60 dark:bg-zinc-900/60 backdrop-blur-lg border border-zinc-200/50 dark:border-zinc-800/50 min-h-[20rem] hover:border-zinc-300/50 dark:hover:border-zinc-700/50 transition-all"
              >
                <div className="flex items-start justify-between mb-auto">
                  <h4 className="text-xl font-normal text-zinc-900 dark:text-zinc-100">
                    {service.title}
                  </h4>
                  <ArrowUpRightIcon className="w-5 h-5 text-zinc-400 transform transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-zinc-900 dark:group-hover:text-zinc-100" />
                </div>
                <p className="text-zinc-600 dark:text-zinc-400 mt-auto font-medium">
                  {service.description}
                </p>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* --- Footer4 Section --- */}
        <div id="sticky-stop">
          <Footer4 />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
